import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { AccordionCollapsible } from 'components/UI/Accordion/AccordionCollapsible';
import { asPerformanceDate, asTime } from 'utils/date-formatter';
import { InputNumber } from 'components/UI/InputNumber/InputNumber';
import { Button } from 'components/UI/Button/Button';
import { ReactComponent as IconApproved } from 'assets/icons/svgs/icon-approved.svg';

export const BookPanelMultiplePriceTypes = (props) => {
  const {
    Performance: {
      PerformanceId,
      Sections,
      ProductionSeasonId,
      DateTime,
      PriceTypes,
      MaxTicketsPerOrder,
    },
    UpsellType,
    handleAddUpsellToCart = () => {},
    isProcessing,
    errorMessage = '',
    numberOfUpsellSelected,
    msgAddedRef,
  } = props;

  const [updateCartBtnDisabled, setUpdateCartBtnDisabled] = useState(true);
  const [bookPanelIsOpen, setBookPanelIsOpen] = useState(false);
  const [clickedUpdateButtonEl, setClickedUpdateButtonEl] = useState();
  const [totalSeatsSelected, setTotalSeatsSelected] = useState(numberOfUpsellSelected);
  const defaultSelectedUpsells = PriceTypes.map((priceType) => {
    let upsellObject;

    if (priceType.NumberOfSeatSelected > 0) {
      upsellObject = {
        NumSeats: priceType.NumberOfSeatSelected,
        PriceTypeId: priceType.Id,
      };
    }

    return upsellObject;
  }).filter((item) => item !== undefined);
  const [selectedUpsells, setSelectedUpsells] = useState(defaultSelectedUpsells);
  const menuOpenClass = bookPanelIsOpen ? 'book-button--open' : '';
  const classes = ['btn btn-secondary book-button', menuOpenClass].join(' ');

  const getSumByKey = (arr, key) =>
    arr.reduce((accumulator, current) => accumulator + Number(current[key]), 0);

  const bookButtonClickHandler = () => {
    setBookPanelIsOpen((prevState) => !prevState);
  };

  const addToUpsellsSelection = (priceTypeId, numSeats) => {
    let updatedUpsellsArr = [];

    const seatAlreadySelected = selectedUpsells.some(
      (seatType) => seatType.PriceTypeId === priceTypeId
    );

    if (seatAlreadySelected) {
      if (numSeats === 0) {
        updatedUpsellsArr = selectedUpsells.filter(
          (seatType) => seatType.PriceTypeId !== priceTypeId
        );
      } else {
        updatedUpsellsArr = selectedUpsells.map((seatType) => {
          const matchingSeatId = seatType.PriceTypeId === priceTypeId;
          return {
            ...seatType,
            NumSeats: matchingSeatId ? numSeats : seatType.NumSeats,
          };
        });
      }
    } else {
      updatedUpsellsArr = [
        ...selectedUpsells,
        {
          PriceTypeId: priceTypeId,
          NumSeats: numSeats,
        },
      ];
    }

    setSelectedUpsells(updatedUpsellsArr);

    const totalOrderPerPriceType = getSumByKey(updatedUpsellsArr, 'NumSeats');
    setTotalSeatsSelected(totalOrderPerPriceType);
  };

  const updateUpsellsSelection = (priceTypeId, numSeats) => {
    setUpdateCartBtnDisabled(false);
    addToUpsellsSelection(priceTypeId, numSeats);
  };

  const updateCartButtonClickHandler = (event) => {
    setClickedUpdateButtonEl(event.target);

    const requestPayload = {
      PerformanceId,
      PriceTypes: selectedUpsells,
      ProdSeasonId: ProductionSeasonId,
      ZoneId: Sections[0].ZoneId,
    };

    handleAddUpsellToCart(requestPayload, msgAddedRef.current);
  };

  useEffect(() => {
    if (isProcessing) {
      setUpdateCartBtnDisabled(true);
    } else {
      // Reset the button element clicked when done processing
      setClickedUpdateButtonEl(null);
    }

    const buttonText = isProcessing ? 'Processing...' : 'Update the cart';
    if (clickedUpdateButtonEl) {
      clickedUpdateButtonEl.innerHTML = buttonText;
    }
  }, [isProcessing]);

  useEffect(() => {
    setUpdateCartBtnDisabled(true);
  }, []);

  return (
    <>
      <span className="item-price" />
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={bookPanelIsOpen && 'true'}
        className={classes}
        onClick={bookButtonClickHandler}
        data-testid="upsells-book-btn"
      >
        Book <span className="caret" />
      </button>

      <AccordionCollapsible extendClass="book-item-panel" isActive={bookPanelIsOpen}>
        <div className="panel-body row">
          <div className="col-sm-8 col-lg-12">
            <div className="row">
              <div className="col-sm-12 col-lg-5">
                {(UpsellType === 'Performance' || UpsellType === 'Lunchbox') && (
                  <div className="form-group">
                    <label>Date &amp; Time</label>
                    <p>
                      {asPerformanceDate(DateTime)} at {asTime(DateTime)}
                    </p>
                    {UpsellType === 'Performance' && (
                      <>
                        <label>Section</label>
                        <p>{Sections[0].Description}</p>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="col-sm-12 col-lg-6 col-lg-offset-1 multiple-items">
                <label>Select ticket types</label>
                {PriceTypes.map((priceType) => {
                  const { Id, Description, PriceRange, NumberOfSeatSelected, TicketMultiple } =
                    priceType;
                  const ptTicketMultiple = TicketMultiple || 1;

                  return (
                    <div className="form-group select-item" key={Id}>
                      <div className="ticket-price">
                        <span className="ticket-type">{Description}</span>
                        <span className="price">{PriceRange}</span>
                      </div>

                      <InputNumber
                        id={Id}
                        maxValue={MaxTicketsPerOrder}
                        incrementChangeHandler={updateUpsellsSelection}
                        decrementChangeHandler={updateUpsellsSelection}
                        defaultValue={NumberOfSeatSelected}
                        operandIncrement={ptTicketMultiple}
                        operandDecrement={ptTicketMultiple}
                        totalSeatsSelected={totalSeatsSelected}
                        onChangeHandler={updateUpsellsSelection}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="col-sm-3 col-sm-offset-1 col-lg-11 text-right">
            <Button
              extendClass={`btn-small btn-add ${updateCartBtnDisabled ? 'disabled' : ''}`}
              onClick={updateCartButtonClickHandler}
              variant="primary"
              disabled={updateCartBtnDisabled}
            >
              <span>Update the cart</span>
            </Button>

            {errorMessage && (
              <div>
                <p className="error-message text-right">{errorMessage}</p>
              </div>
            )}

            <div className="msg-added" ref={msgAddedRef}>
              {numberOfUpsellSelected > 0 && (
                <>
                  <IconApproved className="icon fill-teal" />
                  {numberOfUpsellSelected} added to cart
                </>
              )}
            </div>
          </div>
        </div>
      </AccordionCollapsible>
    </>
  );
};

BookPanelMultiplePriceTypes.propTypes = {
  Performance: PropTypes.object,
  UpsellType: PropTypes.string,
  handleAddUpsellToCart: PropTypes.func,
  isProcessing: PropTypes.bool,
  errorMessage: PropTypes.string,
  numberOfUpsellSelected: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  msgAddedRef: PropTypes.object,
};
